import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Check } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { errText } from '../../utils/errors';
import { fetchDiseaseGroups } from '../volunteer/BooleanObsGroups';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name_es: '', name_en: '', order: 0, active: true };

export function DiseaseGroupsManager({ lang }) {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const es = lang === 'es';

  const load = () => fetchDiseaseGroups(true).then(setGroups);
  useEffect(() => { load(); }, []);

  const openForm = (g) => { setEditing(g); setForm(g ? { name_es: g.name_es, name_en: g.name_en || '', order: g.order || 0, active: g.active !== false } : { ...EMPTY, order: groups.length + 1 }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await axios.put(`${API_URL}/api/config/disease-groups/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/config/disease-groups`, form, { withCredentials: true });
      toast.success(es ? 'Grupo guardado' : 'Group saved');
      setOpen(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  return (
    <div className="mb-6" data-testid="disease-groups-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-[0.05em]">{es ? 'Grupos de enfermedad' : 'Disease groups'}</h3>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-sm" onClick={() => openForm(null)} data-testid="disease-group-create-btn"><Plus size={14} />{es ? 'Nuevo grupo' : 'New group'}</Button>
      </div>
      <p className="text-[11px] text-stone-500 mb-2">{es ? 'Se usan en las preguntas "Sí/No + observación" con selector de grupo (p. ej. Enfermedades crónicas) y en los filtros de Reclutamiento y Protocolo. Desactiva en lugar de borrar para conservar los datos históricos.' : 'Used by "Yes/No + note" questions with group selector and in Recruitment/Protocol filters. Deactivate instead of deleting to keep history.'}</p>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <Table className="dense-table">
          <TableHeader><TableRow>
            <TableHead className="w-12">#</TableHead><TableHead>{es ? 'Nombre ES' : 'Name ES'}</TableHead><TableHead>{es ? 'Nombre EN' : 'Name EN'}</TableHead>
            <TableHead className="w-24">{es ? 'Estado' : 'Status'}</TableHead><TableHead className="w-12" />
          </TableRow></TableHeader>
          <TableBody>
            {groups.map(g => (
              <TableRow key={g.id} data-testid={`disease-group-row-${g.id}`} className={g.active === false ? 'opacity-50' : ''}>
                <TableCell className="font-mono text-xs">{g.order}</TableCell>
                <TableCell className="text-sm">{g.name_es}</TableCell>
                <TableCell className="text-sm">{g.name_en}</TableCell>
                <TableCell>{g.active === false ? <Badge variant="outline" className="text-[10px]">{es ? 'Inactivo' : 'Inactive'}</Badge> : <Check size={14} className="text-green-600" />}</TableCell>
                <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openForm(g)} data-testid={`disease-group-edit-${g.id}`}><Pencil size={14} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? (es ? 'Editar grupo' : 'Edit group') : (es ? 'Nuevo grupo' : 'New group')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>{es ? 'Nombre (ES)' : 'Name (ES)'}</Label><Input data-testid="disease-group-name-es" value={form.name_es} onChange={e => setForm(p => ({ ...p, name_es: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{es ? 'Nombre (EN)' : 'Name (EN)'}</Label><Input data-testid="disease-group-name-en" value={form.name_en} onChange={e => setForm(p => ({ ...p, name_en: e.target.value }))} /></div>
            <div className="flex items-center gap-6">
              <div className="space-y-1.5"><Label>{es ? 'Orden' : 'Order'}</Label><Input type="number" className="w-24" data-testid="disease-group-order" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} /></div>
              <div className="flex items-center gap-2 pt-5"><Switch data-testid="disease-group-active" checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} /><Label>{es ? 'Activo' : 'Active'}</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{es ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="disease-group-save" disabled={saving || !form.name_es.trim()} onClick={save} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (es ? 'Guardar' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
